import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Card, Slide } from '@mui/material';
import { getProducts } from '../firebase/products';
import { Product } from '../types';

const FAKE_USERS = [
  'Alex', 'Maria', 'Juan', 'Sofia', 'Carlos', 'Ana', 'David', 'Laura',
  'Pablo', 'Lucia', 'Diego', 'Elena', 'Javier', 'Carmen', 'Daniel', 'Paula',
  'therrera', 'bigotes_fan', 'gamer23', 'streamer_pro'
];

interface NotificationData {
  product: Product;
  user: string;
  timestamp: number;
}

const LiveSalesNotification = () => {
  const [visible, setVisible] = useState(false);
  const [currentNotification, setCurrentNotification] = useState<NotificationData | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const queueRef = useRef<NotificationData[]>([]);
  const processingRef = useRef(false);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsData = await getProducts();
        if (productsData.length > 0) {
          setProducts(productsData);
        }
      } catch (error) {
        console.error('Error fetching products for notifications:', error);
      }
    };

    fetchProducts();
  }, []);

  const processQueue = () => {
    if (processingRef.current || queueRef.current.length === 0) return;

    processingRef.current = true;
    const nextNotification = queueRef.current.shift();

    if (nextNotification) {
      setCurrentNotification(nextNotification);
      setVisible(true);

      // Hide after 5 seconds
      setTimeout(() => {
        setVisible(false);
        // Wait for exit animation then process next
        setTimeout(() => {
          processingRef.current = false;
          processQueue();
        }, 500); 
      }, 5000);
    } else {
      processingRef.current = false;
    }
  };

  const addToQueue = (notification: NotificationData) => {
    queueRef.current.push(notification);
    if (!visible && !processingRef.current) {
      processQueue();
    }
  };

  // Listener for real "Add to Cart" events
  useEffect(() => {
    const handleCartItemAdded = (event: CustomEvent) => {
      const { product, user } = event.detail;
      addToQueue({
        product,
        user: user || 'Tú',
        timestamp: Date.now()
      });
    };

    window.addEventListener('cart-item-added' as any, handleCartItemAdded as EventListener);

    return () => {
      window.removeEventListener('cart-item-added' as any, handleCartItemAdded as EventListener);
    };
  }, []);

  // Simulated fake events
  useEffect(() => {
    if (products.length === 0) return;

    const triggerFakeNotification = () => {
      const randomProduct = products[Math.floor(Math.random() * products.length)];
      const randomUser = FAKE_USERS[Math.floor(Math.random() * FAKE_USERS.length)];

      addToQueue({
        product: randomProduct,
        user: randomUser,
        timestamp: Date.now()
      });
    };

    // Initial delay
    const initialTimeout = setTimeout(() => {
      triggerFakeNotification();
    }, 5000);

    // Random interval between 15 and 45 seconds
    const interval = setInterval(() => {
      triggerFakeNotification();
    }, Math.random() * (45000 - 15000) + 15000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [products]);

  if (!currentNotification) return null;

  return (
    <Slide direction="right" in={visible} mountOnEnter unmountOnExit>
      <Box
        sx={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 9999,
        }}
      >
        <Card
          sx={{
            display: 'flex',
            alignItems: 'center',
            padding: '12px',
            backgroundColor: 'rgba(10, 10, 10, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid #9d4edd', // Neon purple border
            borderRadius: '16px',
            boxShadow: '0 0 20px rgba(157, 78, 221, 0.4)',
            maxWidth: '350px',
            color: 'white',
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '12px',
              overflow: 'hidden',
              border: '2px solid white',
              marginRight: '15px',
              flexShrink: 0,
            }}
          >
            <img
              src={currentNotification.product.images.find(img => img.isPrimary)?.url || currentNotification.product.images[0]?.url}
              alt={currentNotification.product.name}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
          
          <Box>
            <Typography variant="body2" sx={{ fontFamily: 'Rajdhani, sans-serif', fontSize: '0.9rem', color: '#ccc' }}>
              <span style={{ color: '#00ffff', fontWeight: 'bold' }}>{currentNotification.user}</span> añadió al carrito:
            </Typography>
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontFamily: 'Orbitron, sans-serif', 
                fontWeight: 'bold', 
                fontSize: '1rem',
                color: 'white',
                lineHeight: 1.2,
                mt: 0.5
              }}
            >
              {currentNotification.product.name}
            </Typography>
            <Typography variant="caption" sx={{ color: '#00ff00', fontSize: '0.8rem' }}>
              Hace unos segundos
            </Typography>
          </Box>
        </Card>
      </Box>
    </Slide>
  );
};

export default LiveSalesNotification;
